import { Controller } from "@hotwired/stimulus"

// Lendflow Environment Management Controller
// Handles environment-specific actions (test connection, activate, etc.)
// Import modal functionality is delegated to the reusable import-modal controller
export default class extends Controller {
  static targets = [
    "connectionStatus",
    "testButton",
    "testButtonText",
    "testSpinner",
    "templateIdsInput",
    "importModal"
  ]

  connect() {
    console.log("Lendflow Environments controller connected")
  }

  // ========================================
  // Environment Management Methods
  // ========================================

  // Test connection for a specific environment
  async testConnection(event) {
    event.preventDefault()

    const button = event.currentTarget
    const environmentId = button.dataset.environmentId
    const statusTarget = this.findConnectionStatus(environmentId)

    // Show loading state
    this.setButtonLoading(button, true)
    this.updateConnectionStatus(statusTarget, 'testing', 'Testing...')

    try {
      const response = await fetch(`/admin/lendflow/environments/${environmentId}/test_connection`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok && result.success) {
        this.updateConnectionStatus(statusTarget, 'success', 'Connected')
        this.showNotification('success', result.message || 'Connection test successful')

        // Update duration display if available
        if (result.duration_ms) {
          this.updateConnectionStatus(statusTarget, 'success', `Connected (${result.duration_ms}ms)`)
        }
      } else {
        this.updateConnectionStatus(statusTarget, 'error', 'Failed')
        this.showNotification('error', result.error || 'Connection test failed')
      }
    } catch (error) {
      console.error('Connection test error:', error)
      this.updateConnectionStatus(statusTarget, 'error', 'Error')
      this.showNotification('error', 'Network error during connection test')
    } finally {
      this.setButtonLoading(button, false)
    }
  }

  // Activate environment
  async activate(event) {
    event.preventDefault()

    const button = event.currentTarget
    const environmentId = button.dataset.environmentId

    button.disabled = true

    try {
      const response = await fetch(`/admin/lendflow/environments/${environmentId}/activate`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', result.message || 'Environment activated')
        window.location.reload()
      } else {
        this.showNotification('error', result.error || 'Failed to activate environment')
        button.disabled = false
      }
    } catch (error) {
      console.error('Activate error:', error)
      this.showNotification('error', 'Network error occurred')
      button.disabled = false
    }
  }

  // Deactivate environment
  async deactivate(event) {
    event.preventDefault()

    const button = event.currentTarget
    const environmentId = button.dataset.environmentId

    button.disabled = true

    try {
      const response = await fetch(`/admin/lendflow/environments/${environmentId}/deactivate`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', result.message || 'Environment deactivated')
        window.location.reload()
      } else {
        this.showNotification('error', result.error || 'Failed to deactivate environment')
        button.disabled = false
      }
    } catch (error) {
      console.error('Deactivate error:', error)
      this.showNotification('error', 'Network error occurred')
      button.disabled = false
    }
  }

  // Set as default environment
  async setDefault(event) {
    event.preventDefault()

    const button = event.currentTarget
    const environmentId = button.dataset.environmentId

    button.disabled = true

    try {
      const response = await fetch(`/admin/lendflow/environments/${environmentId}/set_default`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', result.message || 'Default environment updated')
        window.location.reload()
      } else {
        this.showNotification('error', result.error || 'Failed to set default environment')
        button.disabled = false
      }
    } catch (error) {
      console.error('Set default error:', error)
      this.showNotification('error', 'Network error occurred')
      button.disabled = false
    }
  }

  // Delete environment with confirmation
  async deleteEnvironment(event) {
    event.preventDefault()

    const button = event.currentTarget
    const environmentId = button.dataset.environmentId
    const environmentName = button.dataset.environmentName || 'this environment'

    if (!confirm(`Are you sure you want to delete "${environmentName}"? This action cannot be undone.`)) {
      return
    }

    button.disabled = true

    try {
      const response = await fetch(`/admin/lendflow/environments/${environmentId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })

      const result = await response.json()

      if (response.ok) {
        this.showNotification('success', result.message || 'Environment deleted')
        window.location.href = '/admin/lendflow/environments'
      } else {
        this.showNotification('error', result.error || 'Failed to delete environment')
        button.disabled = false
      }
    } catch (error) {
      console.error('Delete error:', error)
      this.showNotification('error', 'Network error occurred')
      button.disabled = false
    }
  }

  // ========================================
  // Import Modal Methods (delegates to modal controller)
  // ========================================

  openImportModal(event) {
    if (event) event.preventDefault()

    if (this.hasImportModalTarget) {
      // The modal component has its own modal controller - call its open method
      const modalController = this.application.getControllerForElementAndIdentifier(
        this.importModalTarget,
        'modal'
      )
      if (modalController) {
        modalController.open()
      } else {
        // Fallback to direct class manipulation
        this.importModalTarget.classList.remove('hidden')
        document.body.style.overflow = 'hidden'
      }
    }
  }

  closeImportModal(event) {
    if (event) event.preventDefault()

    if (this.hasImportModalTarget) {
      // The modal component has its own modal controller - call its close method
      const modalController = this.application.getControllerForElementAndIdentifier(
        this.importModalTarget,
        'modal'
      )
      if (modalController) {
        modalController.close()
      } else {
        // Fallback to direct class manipulation
        this.importModalTarget.classList.add('hidden')
        document.body.style.overflow = ''
      }
    }
  }

  // ========================================
  // Other Utility Methods
  // ========================================

  // Toggle password visibility
  togglePasswordVisibility(event) {
    const button = event.currentTarget
    const targetId = button.dataset.target
    const input = document.getElementById(targetId)

    if (input) {
      if (input.type === 'password') {
        input.type = 'text'
        button.querySelector('svg')?.classList.add('text-emerald-500')
      } else {
        input.type = 'password'
        button.querySelector('svg')?.classList.remove('text-emerald-500')
      }
    }
  }

  // Validate template IDs JSON
  validateTemplateIds(event) {
    const input = event.currentTarget
    const value = input.value.trim()

    if (!value) {
      input.classList.remove('border-red-500', 'border-green-500')
      return
    }

    try {
      JSON.parse(value)
      input.classList.remove('border-red-500')
      input.classList.add('border-green-500')
    } catch (e) {
      input.classList.remove('border-green-500')
      input.classList.add('border-red-500')
    }
  }

  // ========================================
  // Helper Methods
  // ========================================

  findConnectionStatus(environmentId) {
    return document.querySelector(`[data-connection-status="${environmentId}"]`)
  }

  updateConnectionStatus(element, status, text) {
    if (!element) return

    // Remove all status classes
    element.classList.remove('bg-green-900/20', 'text-green-400',
                             'bg-red-900/20', 'text-red-400',
                             'bg-yellow-900/20', 'text-yellow-400',
                             'bg-gray-900/20', 'text-gray-400')

    // Add appropriate classes
    switch (status) {
      case 'success':
        element.classList.add('bg-green-900/20', 'text-green-400')
        break
      case 'error':
        element.classList.add('bg-red-900/20', 'text-red-400')
        break
      case 'testing':
        element.classList.add('bg-yellow-900/20', 'text-yellow-400')
        break
      default:
        element.classList.add('bg-gray-900/20', 'text-gray-400')
    }

    element.textContent = text
  }

  setButtonLoading(button, loading) {
    if (loading) {
      button.disabled = true
      button.dataset.originalText = button.textContent
      button.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Testing...
      `
    } else {
      button.disabled = false
      button.textContent = button.dataset.originalText || 'Test Connection'
    }
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  showNotification(type, message) {
    // Create notification element
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 max-w-sm w-full bg-gray-800 shadow-lg rounded-lg pointer-events-auto border-l-4`

    const borderColors = {
      success: 'border-green-500',
      error: 'border-red-500',
      warning: 'border-yellow-500',
      info: 'border-blue-500'
    }

    notification.classList.add(borderColors[type] || borderColors.info)

    const iconColors = {
      success: 'text-green-400',
      error: 'text-red-400',
      warning: 'text-yellow-400',
      info: 'text-blue-400'
    }

    const icons = {
      success: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
      error: 'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
      warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
      info: 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
    }

    notification.innerHTML = `
      <div class="p-4">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 ${iconColors[type]}" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="${icons[type]}" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3 w-0 flex-1">
            <p class="text-sm font-medium text-white capitalize">${type}</p>
            <p class="mt-1 text-sm text-gray-300">${message}</p>
          </div>
          <div class="ml-4 flex-shrink-0 flex">
            <button type="button" class="bg-gray-800 rounded-md inline-flex text-gray-400 hover:text-gray-200 focus:outline-none" onclick="this.closest('.fixed').remove()">
              <span class="sr-only">Close</span>
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    document.body.appendChild(notification)

    // Auto-remove after delay
    const autoRemoveDelay = type === 'error' ? 8000 : 5000
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove()
      }
    }, autoRemoveDelay)
  }
}
